import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useRef, useState } from 'react';

import { useAuth } from '@/auth/useAuth';
import { useReadOnly } from '@/auth/useViewingAs';
import { UnsavedChangesDialog } from '@/components/form/UnsavedChangesDialog';
import { useUnsavedChanges } from '@/components/form/useUnsavedChanges';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { PageHeader } from '@/components/ui/PageHeader';
import { CardSkeleton } from '@/components/ui/Skeletons';
import { useToast } from '@/components/ui/useToast';
import { isWritable } from '@/lib/organization';

import { ArcDetailsRow } from './ArcDetailsRow';
import { ArcHeaderCard } from './ArcHeaderCard';
import { AddContentDialog } from './AddContentDialog';
import { ArcPreviewDialog } from './ArcPreviewDialog';
import { PREVIEW_FAILED } from './arcPreview';
import {
  isRefusal,
  itemTitle,
  serverSentence,
  type ArcItem,
  type ArcSection,
} from './arcTypes';
import {
  addItem,
  addSection,
  deleteSection,
  moveItem,
  removeItem,
  renameSection,
  reorderItems,
  reorderSections,
  setDetails,
  setHeaderAction,
  setImage,
  setIntro,
  setSectionVisible,
  toBody,
  type WorkingDraft,
} from './draftEdits';
import { EditDetailsDialog } from './EditDetailsDialog';
import { EditHeaderDialog, type HeaderChanges } from './EditHeaderDialog';
import { HeaderActionsCard } from './HeaderActionsCard';
import { MoveItemDialog } from './MoveItemDialog';
import { NewSectionDialog } from './NewSectionDialog';
import { ChangeMarkers, PublishBar } from './PublishBar';
import { RenameSectionDialog } from './RenameSectionDialog';
import { newerVersionSentence } from './newerVersion';
import { publishWarnings } from './publishWarnings';
import { SectionsList } from './SectionsList';
import { useArc, useArcOrganizationId } from './useArc';
import { useArcVersion } from './useArcVersion';
import { useArcMutations } from './useArcMutations';
import { useArcPreview } from './useArcPreview';
import { useDraft } from './useDraft';

/** The same words the navigation's hint uses for Manage ARC. */
export const ARC_BUILDER_HINT =
  'Arrange standard and custom sections in the order supporters will see them.';

/** The banner's second sentence (B33): what loading the newer version costs. */
const NEWER_VERSION_ADVICE =
  'Load it to keep editing; your unsaved changes will be lost.';

/** What is waiting for a yes in a confirm dialog. */
type Asking =
  | { kind: 'publish' }
  | { kind: 'unpublish' }
  | { kind: 'delete'; section: ArcSection }
  | { kind: 'remove'; section: ArcSection; item: ArcItem }
  | null;

/** Which form dialog is open. */
type Editing =
  | { kind: 'details' }
  | { kind: 'header' }
  | { kind: 'new-section' }
  | { kind: 'rename'; section: ArcSection }
  | { kind: 'move'; section: ArcSection; item: ArcItem }
  | { kind: 'add'; sectionId: string }
  | null;

/** Placeholders the size of the loaded page, so nothing jumps when it arrives. */
function LoadingCards() {
  return (
    <Box data-testid="arc-builder-loading" sx={{ display: 'grid', gap: 2 }}>
      <CardSkeleton lines={2} label="Loading the ARC" minHeight={{ xs: 104, sm: 88 }} />
      <CardSkeleton lines={4} minHeight={{ xs: 236, sm: 212 }} />
      <CardSkeleton lines={3} minHeight={{ xs: 232, sm: 196 }} />
      {Array.from({ length: 6 }, (_, index) => (
        <CardSkeleton key={index} lines={1} minHeight={{ xs: 112, sm: 64 }} />
      ))}
    </Box>
  );
}

/**
 * The ARC Builder: how an organization's Actionable Resource Center looks in
 * the app, and the controls to change it.
 *
 * Every edit changes the page's working copy only (E.14, decided by Sebin on
 * 6 October: no autosave). "Unsaved changes" shows until Save Draft sends the
 * whole copy in one request, and leaving the page before that asks first. The
 * app shows the last published copy until Publish Changes, which saves any
 * unsaved changes first. Admins and Super Admins may change the ARC. Staff
 * viewing the organization, and anybody in a suspended or deactivated one,
 * see the same page with every control switched off.
 *
 * Day 25 of the plan: the toggles, hide and show, delete and remove, and
 * publish with its refusal. Day 26: Save Draft, reordering sections and
 * content (drag, keyboard, Move buttons), moving content to another section,
 * and the dialogs - new section, rename, details, and the header with its
 * images and introductory text. Day 27: the Add Content picker. Group 7:
 * Preview, which anyone who may read the ARC may open. Group 6: the switches
 * follow their links, the publish dialog's warnings, and Unpublish ARC.
 */
export function ArcBuilderPage() {
  const { user } = useAuth();
  const viewing = useReadOnly();
  const ownStatus = user?.organizations.find((o) => o.id === user.organizationId)?.status;
  const readOnly = viewing || !isWritable(ownStatus);

  const toast = useToast();
  const arc = useArc();
  const { draft, dirty, edit, discard } = useDraft(arc.data);
  const writes = useArcMutations(discard);
  const unsavedPrompt = useUnsavedChanges(dirty);
  const [asking, setAsking] = useState<Asking>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const close = () => setEditing(null);
  const preview = useArcPreview();
  const [previewing, setPreviewing] = useState(false);

  /*
   * Someone else's save (arc-and-discovery-hardening, D25 to D29). The page
   * checks the version every 15 seconds. Its working copy keeps the version
   * it was loaded at, so a newer one shows up as newer even after the ARC is
   * refetched underneath an edited copy.
   */
  const version = useArcVersion(useArcOrganizationId(), Boolean(arc.data)).data;
  const base = draft?.arc.draft_version;
  const newer =
    version !== undefined && base !== undefined && version.draft_version > base;
  // "Keep editing for now": the version the banner was put away for (B35).
  const [dismissedFor, setDismissedFor] = useState<number | null>(null);
  const following = useRef<number | null>(null);
  const { refetch } = arc;

  /** Drops the working copy for the latest saved draft (B34, B30). */
  const loadLatest = () => {
    // The version being loaded is not followed again by the effect below,
    // which would load it a second time and say "Updated" for it.
    if (version) following.current = version.draft_version;
    discard();
    writes.clearConflict();
    setDismissedFor(null);
    void refetch();
  };

  // Nothing unsaved, or nothing that may be edited: follow the newer version
  // at once and say so (B36).
  useEffect(() => {
    if (!newer || (dirty && !readOnly)) return;
    if (following.current === version.draft_version) return;
    following.current = version.draft_version;
    discard();
    writes.clearConflict();
    void refetch().then(() => toast.show({ message: 'Updated to the latest version.' }));
  }, [newer, dirty, readOnly, version, discard, refetch, toast, writes]);

  // Published or unpublished elsewhere, the version unchanged (D24): the bar
  // follows, and the working copy keeps its edits (B37).
  const savedStatus = arc.data?.status;
  const savedPublishedAt = arc.data?.published_at ?? null;
  useEffect(() => {
    if (!version || savedStatus === undefined) return;
    if (
      version.status !== savedStatus ||
      (version.published_at ?? null) !== savedPublishedAt
    ) {
      void refetch();
    }
  }, [version, savedStatus, savedPublishedAt, refetch]);

  const bannerShown =
    newer &&
    dirty &&
    !readOnly &&
    !writes.conflict &&
    (dismissedFor === null || version.draft_version > dismissedFor);

  const record = draft?.arc;
  // The publish dialog's warnings come from the working copy: unsaved edits
  // are saved first, so they are what the app will show (D7).
  const warnings = record && asking?.kind === 'publish' ? publishWarnings(record) : [];
  // The picker reads its section from the working copy, so an add shows at once.
  const addingTo =
    editing?.kind === 'add'
      ? record?.sections.find((section) => section.id === editing.sectionId)
      : undefined;
  // An edit made while a save is on its way would be lost when its reply lands.
  const busy =
    writes.saveDraft.isPending || writes.publish.isPending || writes.unpublish.isPending;
  const locked = readOnly || busy;
  const change = (step: (current: WorkingDraft) => WorkingDraft) => {
    if (!locked) edit(step);
  };

  /** Sends the working copy as it stands, unsaved edits included (B15, B17). */
  const openPreview = () => {
    if (!draft) return;
    preview.mutate(toBody(draft));
    setPreviewing(true);
  };
  const closePreview = () => {
    setPreviewing(false);
    preview.reset();
  };

  const save = () => {
    if (!draft) return;
    writes.saveDraft.mutate(toBody(draft), {
      onSuccess: () => toast.show({ message: 'Draft saved.' }),
    });
  };

  const applyHeader = ({ intro, logo, cover }: HeaderChanges) =>
    change((current) => {
      let next = current;
      if (intro) next = setIntro(next, intro.content, intro.images);
      if (logo !== undefined) next = setImage(next, 'logo', logo);
      if (cover !== undefined) next = setImage(next, 'cover', cover);
      return next;
    });

  const confirm = () => {
    if (!asking) return;
    if (asking.kind === 'publish') {
      if (!draft) return;
      writes.publish.mutate(
        { unsaved: dirty ? toBody(draft) : null, version: draft.arc.draft_version },
        {
          onSettled: () => setAsking(null),
          onSuccess: () => toast.show({ message: 'The ARC is published.' }),
        },
      );
      return;
    }
    if (asking.kind === 'unpublish') {
      writes.unpublish.mutate(undefined, {
        onSettled: () => setAsking(null),
        onSuccess: () => toast.show({ message: 'The ARC is unpublished.' }),
      });
      return;
    }
    const { section } = asking;
    if (asking.kind === 'delete') {
      change((current) => deleteSection(current, section.id));
    } else {
      const { item } = asking;
      change((current) => removeItem(current, section.id, item.id));
    }
    setAsking(null);
  };

  return (
    <Box data-testid="arc-builder-page">
      <PageHeader
        title="ARC Builder"
        description={ARC_BUILDER_HINT}
        titleAdornment={
          <ChangeMarkers
            unsaved={dirty}
            unpublished={arc.data?.has_unpublished_changes ?? false}
          />
        }
        action={
          <PublishBar
            unsaved={dirty}
            disabled={!record || locked}
            saving={writes.saveDraft.isPending}
            publishing={writes.publish.isPending}
            canPreview={Boolean(record) && !busy}
            onSave={save}
            onPublish={() => setAsking({ kind: 'publish' })}
            onPreview={openPreview}
          />
        }
      />

      {writes.conflict ? (
        <Box sx={{ mb: 3 }} data-testid="arc-builder-conflict-section">
          <ErrorState
            title="Someone else saved this ARC after you opened it."
            description={newerVersionSentence(
              writes.conflict.savedBy,
              writes.conflict.savedAt,
            )}
            action={
              <Button
                variant="secondary"
                size="sm"
                onClick={loadLatest}
                data-testid="arc-builder-conflict-load-latest-button"
              >
                Load the latest version
              </Button>
            }
            testId="arc-builder-conflict"
          />
        </Box>
      ) : null}
      {bannerShown ? (
        <Alert
          severity="warning"
          role="status"
          sx={{ mb: 3 }}
          data-testid="arc-builder-newer-version-banner"
        >
          {newerVersionSentence(version.draft_saved_by, version.draft_saved_at)}{' '}
          {NEWER_VERSION_ADVICE}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={loadLatest}
              data-testid="arc-builder-newer-version-load-button"
            >
              Load the latest version
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDismissedFor(version.draft_version)}
              data-testid="arc-builder-newer-version-dismiss-button"
            >
              Keep editing for now
            </Button>
          </Box>
        </Alert>
      ) : null}
      {writes.saveRefusal ? (
        <Box sx={{ mb: 3 }}>
          <ErrorState title={writes.saveRefusal} testId="arc-builder-save" />
        </Box>
      ) : null}
      {writes.publishRefusal ? (
        <Box sx={{ mb: 3 }}>
          <ErrorState title={writes.publishRefusal} testId="arc-builder-publish" />
        </Box>
      ) : null}
      {writes.unpublishRefusal ? (
        <Box sx={{ mb: 3 }}>
          <ErrorState title={writes.unpublishRefusal} testId="arc-builder-unpublish" />
        </Box>
      ) : null}

      {arc.isError ? (
        <ErrorState
          title="The ARC could not be loaded."
          onRetry={() => void arc.refetch()}
          testId="arc-builder"
        />
      ) : !record ? (
        <LoadingCards />
      ) : (
        // One column that may be narrower than its widest card's content: a
        // grid column is otherwise as wide as that, and the page scrolled
        // sideways at 320px (arc-and-discovery-hardening, B28).
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <ArcDetailsRow
            // The status and publish time are the saved ARC's: never edited
            // here, and published elsewhere while this copy is held (B37).
            arc={{
              ...record,
              status: arc.data?.status ?? record.status,
              published_at: arc.data?.published_at ?? record.published_at,
            }}
            readOnly={locked}
            onEdit={() => setEditing({ kind: 'details' })}
            onUnpublish={() => setAsking({ kind: 'unpublish' })}
          />
          <ArcHeaderCard
            arc={record}
            readOnly={locked}
            onEdit={() => setEditing({ kind: 'header' })}
          />
          <HeaderActionsCard
            arc={record}
            disabled={locked}
            onChange={(action, enabled) =>
              change((current) => setHeaderAction(current, action, enabled))
            }
          />
          <SectionsList
            sections={record.sections}
            limits={record.limits}
            readOnly={locked}
            onSetVisible={(section, visible) =>
              change((current) => setSectionVisible(current, section.id, visible))
            }
            onDelete={(section) => setAsking({ kind: 'delete', section })}
            onRemoveItem={(section, item) => setAsking({ kind: 'remove', section, item })}
            onNew={() => setEditing({ kind: 'new-section' })}
            onRename={(section) => setEditing({ kind: 'rename', section })}
            onMoveItem={(section, item) => setEditing({ kind: 'move', section, item })}
            onReorder={(ids) => change((current) => reorderSections(current, ids))}
            onReorderItems={(section, ids) =>
              change((current) => reorderItems(current, section.id, ids))
            }
            onAddContent={(section) => setEditing({ kind: 'add', sectionId: section.id })}
          />
        </Box>
      )}

      {record && editing?.kind === 'details' ? (
        <EditDetailsDialog
          arc={record}
          onApply={(title, description) =>
            change((current) => setDetails(current, title, description))
          }
          onClose={close}
        />
      ) : null}
      {record && editing?.kind === 'header' ? (
        <EditHeaderDialog
          arc={record}
          uploadImage={writes.uploadImage}
          onApply={applyHeader}
          onClose={close}
        />
      ) : null}
      {record && editing?.kind === 'new-section' ? (
        <NewSectionDialog
          sections={record.sections}
          onApply={(kind, name) => change((current) => addSection(current, kind, name))}
          onClose={close}
        />
      ) : null}
      {editing?.kind === 'rename' ? (
        <RenameSectionDialog
          section={editing.section}
          onApply={(name) =>
            change((current) => renameSection(current, editing.section.id, name))
          }
          onClose={close}
        />
      ) : null}
      {record && editing?.kind === 'move' ? (
        <MoveItemDialog
          item={editing.item}
          from={editing.section}
          sections={record.sections}
          limits={record.limits}
          onApply={(sectionId) =>
            change((current) => moveItem(current, editing.item.id, sectionId))
          }
          onClose={close}
        />
      ) : null}

      {addingTo && record ? (
        <AddContentDialog
          section={addingTo}
          arc={record}
          onAdd={(picked) => change((current) => addItem(current, addingTo.id, picked))}
          onClose={close}
        />
      ) : null}
      <ArcPreviewDialog
        open={previewing}
        reply={preview.data}
        failure={preview.isError ? serverSentence(preview.error, PREVIEW_FAILED) : null}
        // A refused copy (4xx) gets the same answer again, so no Try again (B20).
        onRetry={preview.isError && !isRefusal(preview.error) ? openPreview : undefined}
        onClose={closePreview}
      />

      <UnsavedChangesDialog {...unsavedPrompt} />
      <ConfirmDialog
        open={asking?.kind === 'publish'}
        title="Publish the ARC?"
        description={
          dirty
            ? 'Your unsaved changes are saved first. App users will then see the ARC as it is now.'
            : 'App users will see the ARC as it is now.'
        }
        confirmLabel={dirty ? 'Save and publish' : 'Publish'}
        pending={writes.publish.isPending}
        onConfirm={confirm}
        onCancel={() => setAsking(null)}
        testId="arc-builder-publish-dialog"
      >
        {warnings.length > 0 ? (
          // What the app will not show, from the working copy (D7, B11).
          // None blocks: the server refuses only by its one rule and the
          // size limits (B10).
          <Box
            component="ul"
            data-testid="arc-builder-publish-warnings-list"
            sx={{ m: 0, pl: 2.5, display: 'grid', gap: 0.5 }}
          >
            {warnings.map((warning) => (
              <Typography
                component="li"
                variant="body2"
                key={warning.kind}
                data-testid={`arc-builder-publish-warning-${warning.kind}-item`}
              >
                {warning.text}
              </Typography>
            ))}
          </Box>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={asking?.kind === 'unpublish'}
        title="Unpublish the ARC?"
        description="The ARC leaves the app and drops out of suggested organizations and search. Supporters still see the organization in their own list."
        confirmLabel="Unpublish"
        pending={writes.unpublish.isPending}
        onConfirm={confirm}
        onCancel={() => setAsking(null)}
        testId="arc-builder-unpublish-dialog"
      />
      <ConfirmDialog
        open={asking?.kind === 'delete'}
        title="Delete this section?"
        description={
          asking?.kind === 'delete'
            ? `“${asking.section.name}” and its list of content leave the ARC when you save the draft. The content itself is not deleted.`
            : ''
        }
        confirmLabel="Delete section"
        destructive
        onConfirm={confirm}
        onCancel={() => setAsking(null)}
        testId="arc-builder-delete-section-dialog"
      />
      <ConfirmDialog
        open={asking?.kind === 'remove'}
        title="Remove this content from the section?"
        description={
          asking?.kind === 'remove'
            ? `${asking.item.available ? `“${itemTitle(asking.item)}”` : 'This content'} leaves “${asking.section.name}” when you save the draft. The content itself is not deleted.`
            : ''
        }
        confirmLabel="Remove"
        destructive
        onConfirm={confirm}
        onCancel={() => setAsking(null)}
        testId="arc-builder-remove-item-dialog"
      />
    </Box>
  );
}
